import { CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import useTranslations from "@/shared/lang/useTranslations";

export interface AlreadyVerifiedScreenProps {
  /** ISO date the verification was completed, if known. */
  verifiedAt?: string;
  /**
   * Closing line under the description, for a reader with no session: this screen is the end of the road for
   * them, and saying so is what replaces the action they cannot be given.
   */
  note?: ReactNode;
  /**
   * Optional recovery action. The host passes one only when it has resolved a destination — a button that
   * leads nowhere is worse than no button, so this stays undefined for the external reader.
   */
  action?: ReactNode;
}

/** Shown when the user opens a link whose verification is already complete. */
export function AlreadyVerifiedScreen({ verifiedAt, note, action }: AlreadyVerifiedScreenProps) {
  const { t } = useTranslations("verification");
  const description = verifiedAt
    ? t("already_description", { date: new Date(verifiedAt).toLocaleDateString() })
    : undefined;
  return (
    <ColocaEmpty icon={CheckCircle2} title={t("already_title")} description={description}>
      {note ? <p className="text-sm text-muted-foreground">{note}</p> : null}
      {action}
    </ColocaEmpty>
  );
}
